import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import { OrderSideEnum, TimeInForceEnum } from '../../../lib/enums';
import { OrderTypeEnum } from '../../../lib/enums/order-type.enum';
import lang from '../../../lib/language';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { Order } from '../../../lib/models/trading/types';
import { RootState } from '../../../lib/store';
import {
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData , PositionType } from '../../../lib/store/reporting/types';
import { getCurrentBuyingPower } from '../../../lib/store/selectors';
import MarketStateCache from '../../../lib/store-util/MarketStateCache';
import NBBOPriceCache from '../../../lib/store-util/NBBOPriceCache';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency, formatNumber } from '../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';
import { isMarketClosed, isMarketOpen } from '../../../lib/util/MarketDataHelpers';
import { TextHelpers } from '../../../lib/util/TextHelpers';
import { uuidv4 } from '../../../lib/util/tools';
import { checkSymbolPostionType } from '../../../lib/util/trading-position-helpers';
import { convertOrderSideToText } from '../../../lib/util/TradingHelpers';
import { getOrderTotalValue } from '../../../lib/util/TradingValidationsHelper';

import { useMaintenanceMode, useTradingButtonsDisabled } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../shared_components/Button/Button.types';
import { Error, MaintenanceNotice, NotEnoughBuyingPower } from '../..';
import HighLowLimitPriceAlert from '../../HighLowLimitPriceAlert/HighLowLimitPriceAlert';
import SharesCalculator from '../../SharesCalculator/SharesCalculator';
import LimitPriceInput from '../components/LimitPriceInput';
import MarketPriceInfo from '../components/MarketPriceInfo';
import { getNameByOrderType, getValidationPrice } from '../OrderHelpers';
import useValidateOrder from '../useValidateOrder';

import styles from './ModifyOrder.module.scss';

type OwnProps = {
  symbol: string;
  orderSide: string;
  orderType: string;
  initialPrice: number;
  orderQuantity: number;
  parentOrderId: string;
  onCloseModal: () => void;
  setOrder: (order: Order) => void;
  setModifyOrderRequested: (hasModifiedOrder: boolean) => void;
}
type Props = OwnProps & ReduxProps;

function ModifyOrder(props: Props) {
  const {
    symbol,
    positions,
    orderSide,
    orderType,
    buyingPow,
    initialPrice,
    orderQuantity,
    parentOrderId,
    isOpenPositionsReady,
    isPendingOrdersReady,
    accountStatusData,
    setOrder,
    getOrders,
    onCloseModal,
    myStocksUpdate,
    getOpenPositions,
    setModifyOrderRequested,
  } = props;

  const isBuyOrder = orderSide === OrderSideEnum.Buy;

  const { marketState } = MarketStateCache.use();
  const isMaintenance = useMaintenanceMode();
  const marketClosed = isMarketClosed(marketState);
  const showMarketInfoIcon = isMarketOpen(marketState);
  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });

  const [hasUpdatesOrders, setHasUpdatesOrders] = useState<boolean>(false);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [criticalError, setCriticalError] = useState<string>('');
  const [targetPriceError, setTargetPriceError] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [targetPrice, setTargetPrice] = useState<number>(initialPrice);
  const [showBP, setShowBP] = useState<boolean>(true);
  const [openedEnoughBPModal, setOpenedEnoughBPModal] = useState<boolean>(false);
  const [visibleHighLowLimitPriceAlert, setVisibleHighLowLimitPriceAlert] = useState<boolean>(false);
  const nbboData = NBBOPriceCache.use(symbol);
  const { askprice, bidprice } = nbboData;
  const { currentPrice: tradePrice = 0 } = TradePriceCache.use(symbol, 'CreateOrder');
  const validationPirce = getValidationPrice(marketClosed, askprice, bidprice, tradePrice, isBuyOrder);
  const { errorMessage, controlCheck } = useValidateOrder(orderType, validationPirce ?? 0, targetPrice, isBuyOrder);

  const positionType = checkSymbolPostionType(symbol, positions);

  useEffect(() => {
    if (positionType) {
      if ((orderSide === OrderSideEnum.Buy && positionType === PositionType.Sell)
        || (orderSide === OrderSideEnum.Sell && positionType === PositionType.Buy)) {
        setShowBP(false);
      } else {
        setShowBP(true);
      }
    } else {
      setShowBP(orderSide === OrderSideEnum.Buy);
    }
  }, [orderSide, positionType]);

  const totalValue = getOrderTotalValue(
    orderType,
    quantity,
    tradePrice,
    targetPrice,
  );

  useEffect(() => {
    if (hasUpdatesOrders && ((isPendingOrdersReady && isOpenPositionsReady && !marketClosed) || marketClosed)) {
      setModifyOrderRequested(true);
    }
  }, [marketClosed, hasUpdatesOrders, setModifyOrderRequested, isPendingOrdersReady, isOpenPositionsReady]);

  const isValidOrder = useCallback((qty: number, buyingPower: number): boolean => {
    setTargetPriceError(errorMessage);

    const buyingPowerCheck = buyingPower > totalValue;

    if (showBP && (controlCheck && !buyingPowerCheck)) {
      setOpenedEnoughBPModal(true);
    }

    if (!showBP) {
      return (orderQuantity >= qty) && controlCheck;
    }

    return buyingPowerCheck && controlCheck;
  }, [controlCheck, errorMessage, orderQuantity, showBP, totalValue]);

  const onReviewOrderClicked = () => {
    setCriticalError('');
    setTargetPriceError('');

    const price = tradePrice ?? 0;
    if (isValidOrder(quantity, buyingPow)) {
      const orderObject: Order = {};

      if (orderType === OrderTypeEnum.Stop) {
        orderObject.stopPrice = targetPrice;
      }
      orderObject.clientOrderId = uuidv4();
      orderObject.symbol = symbol;
      orderObject.side = orderSide as OrderSideEnum;
      orderObject.orderQty = quantity;
      orderObject.ordType = orderType as OrderTypeEnum;
      orderObject.price = targetPrice;
      orderObject.originalClientOrderId = parentOrderId;

      if (orderType === OrderTypeEnum.Limit && !visibleHighLowLimitPriceAlert && askprice && bidprice) {
        if (
          (isBuyOrder && (orderObject.price > askprice))
          || (!isBuyOrder && (orderObject.price < bidprice))
        ) {
          setVisibleHighLowLimitPriceAlert(
            isBuyOrder ? (orderObject.price > askprice) : (orderObject.price < bidprice),
          );

          return;
        }
      }
      setHasUpdatesOrders(true);
      myStocksUpdate(true);
      if (!marketClosed) {
        getOpenPositions();
      }
      setOrder(orderObject);
      getOrders({
        symbols_StringList: symbol,
        tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
        isPendingOrdersCall: true,
      });
    }
  };

  useEffect(() => {
    if (!isInitialised) {
      setQuantity(orderQuantity);
      setIsInitialised(true);
    }
  }, [isInitialised, orderQuantity]);

  return (
    <>
      <div className={styles.wrapperOrderContainer}>
        <h6>{`${lang.orderMessageTitleModify2(symbol, convertOrderSideToText(orderSide))}`}</h6>
        <div className={styles.content}>
          <div className={styles.wrapperOrderType}>
            <div>{lang.commonTradingOrderType()}</div>
            <div className={styles.rigthAligned}>{getNameByOrderType(orderType)}</div>
          </div>
          <div className={styles.wrapperNumberOfShares}>
            <div className={styles.numberOfShares}>
              <div>{lang.mobileTradingTicketNumberOfShares()}</div>
              {!showBP && (
                <p className={styles.numberOfSharesSubText}>
                  {
                    isBuyOrder
                      ? lang.webTradingShortedStocks(formatNumber(orderQuantity, true))
                      : lang.webTradingAvailableShares(formatNumber(orderQuantity, true))
                  }
                </p>
              )}
            </div>
            <div className={styles.wrapperSharesField}>
              <SharesCalculator
                quantity={quantity}
                setQuantity={setQuantity}
                maxQuantity={!showBP ? orderQuantity : undefined}
              />
            </div>
          </div>
          <LimitPriceInput
            initialPrice={initialPrice}
            orderType={orderType}
            targetPriceError={targetPriceError}
            setTargetPrice={(value) => setTargetPrice(value)}
            setTargetPriceError={(value) => setTargetPriceError(value)}
          />
          <MarketPriceInfo
            askPrice={askprice}
            bidPrice={bidprice}
            isMyAccount
            symbol={symbol}
            tradePrice={tradePrice}
            isMarketOpen={showMarketInfoIcon}
          />
          <div className={styles.expiryDateContainer}>
            <div className={styles.expiryDateText}>
              {lang.headerMyAccountPendingOrderExpiryDate2()}
            </div>
            <div className={styles.rightAligned}>
              {TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay)}
            </div>
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.availableBuyingContainer}>
            <div>{lang.commonTradingOrderValue()}</div>
            <div className={styles.rigthAligned}>
              {formatCurrency(totalValue)}
            </div>
          </div>
          {showBP && (
            <div className={styles.availableBuyingContainer}>
              <div>{lang.mobileOrderCreateBuyingPower()}</div>
              <div className={styles.rigthAligned}>{formatCurrency(buyingPow)}</div>
            </div>
          )}
          {(criticalError.trim() !== '') && (
            <div className={styles.errorWrapper}>
              <Error
                hiddenIcon
                errorMessage={criticalError}
              />
            </div>
          )}
          {' '}

        </div>
        {isMaintenance ? <MaintenanceNotice /> : (
          <div className={styles.buttons}>
            <Button
              fullWidth
              biggerFontSize
              disabled={quantity <= 0 || isButtonsDisabled}
              onClick={onReviewOrderClicked}
            >
              {lang.commonConfirmOrderButton2()}
            </Button>
            <Button
              variant={ButtonTypesEnum.Secondary}
              fullWidth
              biggerFontSize
              disabled={quantity <= 0}
              onClick={onCloseModal}
            >
              {lang.commonCancelMultiple()}
            </Button>
          </div>
        )}
        {openedEnoughBPModal && <NotEnoughBuyingPower closeModal={() => setOpenedEnoughBPModal(false)} />}
        {visibleHighLowLimitPriceAlert && (
          <HighLowLimitPriceAlert
            isBuyOrder={isBuyOrder}
            processModal={() => {
              onReviewOrderClicked();
            }}
            toggleModal={() => setVisibleHighLowLimitPriceAlert(false)}
          />
        )}
      </div>
    </>
  );
}

const mapStateProps = (state: RootState) => ({
  positions: state.reporting.openPositions,
  isOpenPositionsReady: isCallStatusReady(ReportingCall.getOpenPositions, state.reporting.statusByCall),
  isPendingOrdersReady: isCallStatusReady(ReportingCall.getAccountPendingOrders, state.reporting.statusByCall),
  buyingPow: getCurrentBuyingPower(state),
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getOpenPositions: () => dispatch(getPositions()),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ModifyOrder);
